import { observer } from "mobx-react";
import { TMessage } from "../../i18n/tt";
import { Box, Card, VBox } from "../../components-v2/Box";
import { DateInput, MDateInput, MTimeInput, TimeInput } from "../../components-v2/DateInput";
import {
  NumberOfShares,
  TransactionDateAndTimeForm,
  TransactionDateCardModel,
} from "./commonTransactionsDomain";
import { useLocaleFormat, useRootStore, useTranslation } from "../../stores/RootStoreContext";
import { FC } from "react";
import { isMobileView } from "../../utils/screen";
import { DropdownMenu, DropdownMenuItemType } from "../../components-v2/Dropdown";
import { transactionLabel, TransactionType } from "../../types/enums/TransactionType";
import { isFailure, isOk, isPending } from "../../common/common";
import { Alert } from "../../components-v2/Alert";
import { MTextInput } from "../../components-v2/TextInput";
import { NewShareClassController } from "./shareClassDomain";
import { Dialog } from "../../components-v2/Dialog";
import { MSelect } from "../../components-v2/Select";
import { shareTypeLabel } from "../../common/share";
import { compact } from "lodash";
import { useBlocker } from "../../routes/useBlocker";
import { Table } from "../../components-v2/Table";
import { ReplayTransactionError } from "../../stores/TransactionStore";
import { IconButton, LinkButton } from "../../components-v2/Button";
import { adminShareholdersTransaction, openLinkInNewWindow } from "../../utils/navigation";
import { Transaction } from "../../types/Transactions";

export const ShareholdingOverview = observer(
  ({ shareholding }: { shareholding: NumberOfShares[] }) => {
    const { t } = useTranslation();
    const { formatNumber } = useLocaleFormat();
    return (
      <VBox>
        <div>{t("shares-for-sale-overview")}</div>
        <Box>
          <Table variant={"IN_CARD"}>
            <tbody>
              {shareholding.map((s) => (
                <tr key={s.sharetypeCode}>
                  <td>{t(shareTypeLabel(s.sharetypeCode))}</td>
                  <td>{formatNumber(s.numberOfShares)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Box>
      </VBox>
    );
  },
);

export const TransactionDateAndTime = observer(
  ({
    form,
    onChange,
    dateLabel,
  }: {
    form: TransactionDateAndTimeForm;
    onChange?: (date: string, time: string) => void;
    dateLabel?: TMessage;
  }) => {
    const { t } = useTranslation();
    const handleDateChange = (value: string) => {
      form.setTransactionDate(value);
      onChange?.(value, form.transactionTime.inputValue);
    };

    const handleDateChangeAndCommit = (value: string) => {
      form.setAndCommitTransactionDate(value);
      onChange?.(value, form.transactionTime.inputValue);
    };

    const handleTimeChange = (value: string) => {
      form.transactionTime.setInputValue(value);
      onChange?.(form.transactionDate.inputValue, value);
    };
    const label = dateLabel ?? { key: "transactions.transaction-date" };
    return (
      <VBox>
        <Box>
          <label htmlFor={"transactionDate"}>{t(label)}</label>
        </Box>
        <Box>
          <DateInput
            id={"transactionDate"}
            onInput={(event) => handleDateChange(event.target.value)}
            onChange={(event) => handleDateChangeAndCommit(event.input)}
            value={form.transactionDate.inputValue}
            error={!!form.transactionDate.error}
            message={form.transactionDate.error ? t(form.transactionDate.error) : undefined}
            onBlur={() => {
              form.transactionDate.commit();
              form.validate();
            }}
          />
          <TimeInput
            value={form.transactionTime.inputValue}
            onInput={(event) => handleTimeChange(event.target.value)}
            onChange={(event) => handleTimeChange(event.input)}
            error={!!form.transactionTime.error}
            message={form.transactionTime.error ? t(form.transactionTime.error) : undefined}
            onBlur={() => {
              form.transactionTime.commit();
              form.validate();
            }}
          />
        </Box>
      </VBox>
    );
  },
);

export const TransactionDateCard = observer(function TransactionDateCard({
  model,
  transaction,
  dateLabel,
}: {
  model: TransactionDateCardModel;
  transaction?: Transaction;
  dateLabel?: TMessage;
}) {
  const { t } = useTranslation();
  const { formatDateTime } = useLocaleFormat();
  const { shareholdersStore } = useRootStore();
  const form = model;

  return (
    <Card title={t(dateLabel ?? "transactions.transaction-date")}>
      <VBox>
        {model.isPrefilled ? (
          <Box alignItems={"center"}>
            <Box>{formatDateTime(model.combinedDateAndTime)}</Box>
            {transaction && (
              <IconButton
                icon={"edit"}
                tooltip={{ key: "edit" }}
                onClick={() =>
                  shareholdersStore.changeTransactionDate(transaction, {
                    key: "transactions.edit.change-date-info",
                  })
                }
              />
            )}
          </Box>
        ) : (
          <Box>
            <Box>
              <MDateInput model={form.transactionDate} id={"transactionDate"} />
            </Box>
            <Box>
              <MTimeInput model={form.transactionTime} />
            </Box>
          </Box>
        )}
        {form.error ? (
          <Alert type={"error"} message={form.error} />
        ) : form.info ? (
          <Alert message={form.info} type={"info"} />
        ) : null}
      </VBox>
    </Card>
  );
});

export const RegistrationTransactionDateCard = observer(function TransactionDateCard({
  model,
}: {
  model: TransactionDateCardModel;
}) {
  const { t } = useTranslation();
  const form = model;
  return (
    <Card title={t("transactions.common.registrationHeader")}>
      <VBox gap={"md"} marginTop={"md"}>
        <Box>
          <label htmlFor={"transactionDate"}>{t("transactions.common.registrationDate")}</label>
        </Box>
        <Box>
          <Box>
            <MDateInput id={"transactionDate"} model={form.transactionDate} />
            <MTimeInput model={form.transactionTime} />
          </Box>
        </Box>
        {form.error ? (
          <Alert type={"error"} message={form.error} />
        ) : form.info ? (
          <Alert message={form.info} type={"info"} />
        ) : null}
        <Box>{t("transactions.common.registrationText")}</Box>
      </VBox>
    </Card>
  );
});

export const UnsavedChangesPrompt = observer(({ showWhen }: { showWhen: boolean }) => {
  const { t } = useTranslation();
  useBlocker(showWhen, t("transactions.unsaved-changes-prompt"));
  return <></>;
});

export const AddShareRecipient: FC<{
  transactionType: TransactionType;
  onAddNewShareholder?: () => void;
  showAddBuyer?: boolean;
  onAddExistingBuyer?: () => void;
  disabled?: boolean;
}> = observer(
  ({ transactionType, onAddNewShareholder, showAddBuyer = true, onAddExistingBuyer, disabled }) => {
    const { t } = useTranslation();

    const dropdownItems: DropdownMenuItemType[] = compact([
      onAddExistingBuyer && {
        type: "item",
        icon: "suitcaseSimple",
        label: t("transactions.add-shareholder.from-portfolio"),
        onSelect: onAddExistingBuyer,
      },
      onAddNewShareholder && {
        type: "item",
        icon: "plus",
        label: t("shareholders.add"),
        onSelect: onAddNewShareholder,
      },
    ]);

    return (
      <>
        <Box vertical={isMobileView()} wrap>
          <Box>
            {showAddBuyer && (
              <DropdownMenu
                disabled={disabled}
                button={{
                  type: "button",
                  variant: "secondary",
                  label:
                    transactionType === TransactionType.BUY_SELL
                      ? t("transactions.add-buyer")
                      : t("transactions.add-recipient"),
                  fullWidth: true,
                }}
                items={dropdownItems}
              />
            )}
          </Box>
        </Box>
      </>
    );
  },
);

export const NewShareClassDialog: FC<{
  controller: NewShareClassController;
}> = observer(({ controller }) => {
  const { t } = useTranslation();
  return (
    <Dialog
      title={t("shareClasses.shareClassForm.create")}
      onCancel={controller.onClose}
      onSubmit={controller.createShareType}
      maxWidth={"sm"}
      submitLabel={t("save")}
      cancelLabel={t("cancel")}
      fullscreen={isMobileView()}
      isSubmitting={isPending(controller.addShareTypeResult)}
      isCancelDisabled={false}
      isSubmitDisabled={isOk(controller.addShareTypeResult)}
    >
      <VBox paddingBottom={"md"}>
        <h5>{t("choose.new.sharetype")}</h5>
        <MSelect model={controller.shareTypeModel} />
        <Box marginTop={"lg"}>
          <h5>{t("shareClasses.shareClassForm.votingPower")}</h5>
        </Box>
        <Box alignItems={"center"}>
          <div>1</div>
          <div>{t("shareClasses.shareClassForm.sharesEquals")}</div>
          <Box>
            <MTextInput model={controller.numberOfVotesModel} />
          </Box>
          <div>{t("shareClasses.shareClassForm.votes")}</div>
        </Box>
      </VBox>
      <Box grow>
        {isFailure(controller.addShareTypeResult) && (
          <Alert grow type={"error"} message={t("shareClasses.shareClassForm.save.error")} />
        )}
      </Box>
    </Dialog>
  );
});

export const ReplayErrorDialog = observer(function ReplayErrorDialog({
  error,
}: {
  error: ReplayTransactionError;
}) {
  const { t } = useTranslation();
  const { formatDate } = useLocaleFormat();
  const { userStore, transactionStore } = useRootStore();
  return (
    <Dialog
      title={{ key: "transactions.replay-failed.title" }}
      onCancel={transactionStore.clearReplayError}
      cancelLabel={{ key: "close" }}
    >
      <VBox>
        <p>{t("transactions.replay-failed.text")}</p>
        <div>
          <LinkButton
            label={`${t(transactionLabel[error.transactionType])} - ${formatDate(error.transactionDate)}`}
            onClick={() =>
              openLinkInNewWindow(
                adminShareholdersTransaction(userStore.orgNumber, error.transactionId),
              )
            }
            icon={"arrow-square-out"}
          />
        </div>
      </VBox>
    </Dialog>
  );
});
